import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, ClipboardList, Plus } from 'lucide-react'
import {
  useFormularioEncuesta,
  useCrearPasoEncuesta, useEditarPasoEncuesta, useEliminarPasoEncuesta, useReordenarPasosEncuesta,
  useCrearCampoEncuesta, useEditarCampoEncuesta, useEliminarCampoEncuesta, useReordenarCamposEncuesta,
} from '../hooks/useEncuestas'
import { PasoCard } from '../components/formbuilder/PasoCard'
import { PasoDialog } from '../components/formbuilder/PasoDialog'
import { CampoDialog } from '../components/formbuilder/CampoDialog'
import type { PasoFormulario, CampoFormulario, PasoCreatePayload, TipoCampo } from '../types'

export function EncuestaFormBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)

  const { setOverride, clearOverride } = useBreadcrumb()
  useDocumentTitle('Form Builder Encuesta')

  const { data: formulario, isLoading } = useFormularioEncuesta(campaniaId)

  useEffect(() => {
    if (formulario?.campania_titulo) {
      setOverride(`/muestras-medicas/${id}`, formulario.campania_titulo)
    }
    return () => { clearOverride(`/muestras-medicas/${id}`) }
  }, [formulario?.campania_titulo, id, setOverride, clearOverride])

  const crearPaso = useCrearPasoEncuesta(campaniaId)
  const editarPaso = useEditarPasoEncuesta(campaniaId)
  const eliminarPaso = useEliminarPasoEncuesta(campaniaId)
  const reordenarPasos = useReordenarPasosEncuesta(campaniaId)

  const crearCampo = useCrearCampoEncuesta(campaniaId)
  const editarCampo = useEditarCampoEncuesta(campaniaId)
  const eliminarCampo = useEliminarCampoEncuesta(campaniaId)
  const reordenarCampos = useReordenarCamposEncuesta(campaniaId)

  const [pasoDialogOpen, setPasoDialogOpen] = useState(false)
  const [editingPaso, setEditingPaso] = useState<PasoFormulario | null>(null)
  const [campoDialogOpen, setCampoDialogOpen] = useState(false)
  const [editingCampo, setEditingCampo] = useState<CampoFormulario | null>(null)
  const [activePasoId, setActivePasoId] = useState<number>(0)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const handleCreatePaso = async (data: PasoCreatePayload) => {
    await crearPaso.mutateAsync(data)
    setPasoDialogOpen(false)
  }

  const handleEditPaso = async (data: PasoCreatePayload) => {
    if (!editingPaso) return
    await editarPaso.mutateAsync({ pasoId: editingPaso.id, payload: data })
    setEditingPaso(null)
    setPasoDialogOpen(false)
  }

  const handleDeletePaso = (pasoId: number) => {
    if (confirm('¿Eliminar este paso y todos sus campos?')) {
      eliminarPaso.mutate(pasoId)
    }
  }

  const handlePasoDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !formulario) return
    const oldIndex = formulario.pasos.findIndex((p) => p.id === active.id)
    const newIndex = formulario.pasos.findIndex((p) => p.id === over.id)
    const newOrder = arrayMove(formulario.pasos, oldIndex, newIndex)
    reordenarPasos.mutate(newOrder.map((p) => p.id))
  }

  const handleCreateCampo = async (data: {
    nombre_campo: string; etiqueta: string; tipo_campo: TipoCampo
    placeholder?: string; requerido?: boolean; configuracion_extra?: Record<string, unknown>
  }) => {
    await crearCampo.mutateAsync({ ...data, paso_id: activePasoId })
    setCampoDialogOpen(false)
  }

  const handleEditCampoSubmit = async (data: {
    nombre_campo: string; etiqueta: string; tipo_campo: TipoCampo
    placeholder?: string; requerido?: boolean; configuracion_extra?: Record<string, unknown>
  }) => {
    if (!editingCampo) return
    await editarCampo.mutateAsync({ campoId: editingCampo.id, payload: data })
    setEditingCampo(null)
    setCampoDialogOpen(false)
  }

  const handleDeleteCampo = (campoId: number) => {
    if (confirm('¿Eliminar este campo?')) {
      eliminarCampo.mutate(campoId)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/muestras-medicas/${campaniaId}/encuestas`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-teal-500" />
            Form Builder Encuesta
            {formulario && (
              <span className="text-muted-foreground font-normal text-lg">— {formulario.campania_titulo}</span>
            )}
          </h1>
        </div>
        <Button className="bg-teal-800 hover:bg-teal-900 text-white" onClick={() => { setEditingPaso(null); setPasoDialogOpen(true) }}>
          <Plus className="mr-2 h-4 w-4" /> Agregar Paso
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : formulario && formulario.pasos.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePasoDragEnd}>
          <SortableContext items={formulario.pasos.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {formulario.pasos.map((paso) => (
                <PasoCard
                  key={paso.id}
                  paso={paso}
                  onEditPaso={() => { setEditingPaso(paso); setPasoDialogOpen(true) }}
                  onDeletePaso={() => handleDeletePaso(paso.id)}
                  onAddCampo={() => { setActivePasoId(paso.id); setEditingCampo(null); setCampoDialogOpen(true) }}
                  onEditCampo={(campo) => { setActivePasoId(paso.id); setEditingCampo(campo); setCampoDialogOpen(true) }}
                  onDeleteCampo={handleDeleteCampo}
                  onReorderCampos={(orden) => reordenarCampos.mutate(orden)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="text-center py-12">
          <ClipboardList className="mx-auto h-12 w-12 text-teal-500" />
          <h3 className="mt-4 text-lg font-semibold">Sin pasos</h3>
          <p className="mt-2 text-sm text-muted-foreground">Agrega el primer paso de la encuesta</p>
          <Button className="mt-4 bg-teal-800 hover:bg-teal-900 text-white" onClick={() => { setEditingPaso(null); setPasoDialogOpen(true) }}>
            <Plus className="mr-2 h-4 w-4" /> Agregar Paso
          </Button>
        </div>
      )}

      <PasoDialog
        open={pasoDialogOpen}
        onOpenChange={(open) => { setPasoDialogOpen(open); if (!open) setEditingPaso(null) }}
        onSubmit={editingPaso ? handleEditPaso : handleCreatePaso}
        isSubmitting={crearPaso.isPending || editarPaso.isPending}
        paso={editingPaso}
      />

      <CampoDialog
        open={campoDialogOpen}
        onOpenChange={(open) => { setCampoDialogOpen(open); if (!open) setEditingCampo(null) }}
        onSubmit={editingCampo ? handleEditCampoSubmit : handleCreateCampo}
        isSubmitting={crearCampo.isPending || editarCampo.isPending}
        campo={editingCampo}
        pasoId={activePasoId}
      />
    </div>
  )
}
